import { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonIcon,
  formatUnixTime,
  LoadingSpinner,
  Modal,
  WarrantyViewer,
  isSerialNumberValid
} from '@rabbit/elements/shared-components';
import {
  SageConsumerDataShape,
  useGetAuditLogs,
  useGetConsumerData,
  useGetWarrantyHistory,
  useSageAPI,
} from '@rabbit/bizproc/react';
import {
  FBD_Business_User_Invite,
  FBD_CaseFlowPrivateProxy,
  FBD_Retailer_Public,
  NoteEntityType,
  Permissions,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  Warranty,
  WarrantyStatus,
  FBD_AuditLog,
  FBD_HoldingProxy,
} from '@rabbit/data/types';
import RegistrationsOtherProductsSection from '../components/templates/registrations/other-products-table/RegistrationsOtherProductsSection';
import RegistrationsCustDetailsSection from '../components/templates/registrations/customer-details/RegistrationsCustDetailsSection';
import RegistrationsHoldingDetailsSection from '../components/templates/registrations/holding-details/RegistrationsHoldingDetailsSection';
import RegistrationsHoldingSummarySection from '../components/templates/registrations/holding-summary/RegistrationsHoldingSummarySection';
import {
  getFavouriteStore,
  getRetailerLabel,
  getUserPermissions,
  isRegistrationOlderThan7Days
} from '../utils/helpers';
import { checkAuditLogsExistence } from '../utils/audit-log-utils';
import { useTranslation } from 'react-i18next';
import { TenantType } from './RegistrationsView';
import CarRegistrationsHoldingSummarySection from '../components/templates/registrations/holding-summary/CarRegistrationsHoldingSummarySection';
import { UserContext } from '../context/UserContext';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import {
  Bars3BottomLeftIcon,
  ClockIcon,
  Cog8ToothIcon,
  DocumentMinusIcon,
  PencilSquareIcon,
  PrinterIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  BL_Warranty,
  nestApiUpdateHolding,
  UpdateHoldingProxyRequest,
} from '@rabbit/bizproc/core';
import ModalEditWarranty from '../components/organisms/ModalEditWarranty/ModalEditWarranty';
import { AppContext, ConfigInterface } from '@rabbit/app-context';
import { ModalHistoryLog } from '../components/organisms/ModalHistoryLog/ModalHistoryLog';
import ModalNewClaim from '../components/organisms/ModalNewClaim/ModalNewClaim';
import ModalNewClaimVehicle from '../components/organisms/ModalNewClaimVehicle/ModalNewClaimVehicle';
import InstallationDetails from '@rabbit/sage/components/templates/registrations/installation-details/installationDetails.tsx';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import WarrantyDetailsSection from '@rabbit/sage/components/templates/registrations/warranty-details/WarrantyDetailsSection.tsx';
import AdditionalDetailsView from 'libs/elements/shared-components/src/lib/molecules/AdditionalDetailsView';
import RegistrationsWarrantyDetailsSectionCarWarranty from '../components/templates/registrations/RegistrationsWarrantyDetailsSection/RegistrationsWarrantyDetailsSectionCarWarranty';
import NotesDetailContentSection from '@rabbit/sage/components/molecules/Notes/NotesDetailContentSection.tsx';
import Notes from '../components/organisms/Notes/Notes';
import ModalEditDynamicForm, {
  FormSchemaEnum,
} from '../components/organisms/ModalEditDynamicForm/ModalEditDynamicForm';
import ModalCreateNewSelectUser from '../components/organisms/ModalCreateNewSelectUser/ModalCreateNewSelectUser';
import ModalSelectActionType from '../components/organisms/ModalSelectActionType/ModalSelectActionType';
import { ModalNewEditCustomer } from '../components/organisms/ModalNewEditCustomer/ModalNewEditCustomer';
import { OurDateTime } from '@rabbit/utils/ts';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RegistrationsDetailsViewProps {}

export function RegistrationsDetailsView(props: RegistrationsDetailsViewProps) {
  const { t } = useTranslation();
  const { config, tenantInfo } = useContext(AppContext);
  const userContext = useContext(UserContext);
  const activeTenantInfo = userContext?.activeTenantInfo;
  const tenantLink = t('tenantLink');
  const isPartnerToTenant =
    activeTenantInfo?.isPartnerTo?.length &&
    activeTenantInfo?.isPartnerTo?.length > 0 &&
    activeTenantInfo?.isPartnerTo?.includes(tenantLink);
  const userPermissions = getUserPermissions(tenantLink, userContext);
  const { warrantyVoid } = useSageAPI();
  const [historyLog, setHistoryLog] = useState(false);
  const [editRegistration, setEditRegistration] = useState(false);
  const [changeCustomer, setChangeCustomer] = useState(false);
  const [storeAddress, setStoreAddress] = useState<string>('-');
  const [showWarrantyOptions, setShowWarrantyOptions] = useState(false);
  const { consumerPersona } = useGetMySagePersonas();
  const [isVoiding, setIsVoiding] = useState(false);
  const [voidWarranty, setVoidWarranty] = useState<string>();
  const [editWarranty, setEditWarranty] = useState<string>();
  const [confirmation, setConfirmation] = useState('');
  const [newClaimModalOpen, setNewClaimModalOpen] = useState(false);
  const [warrantyDetailsForClaim, setWarrantyDetailsForClaim] =
    useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isAdmin = userContext?.accessToken?.admin ?? false;
  const [shouldRefetch, setShouldRefetch] = useState<number>(0);
  const [checkingClaims, setCheckingClaims] = useState(false);
  const [hasLogs, setHasLogs] = useState(false);
  const [hasWarrantyLogs, setHasWarrantyLogs] = useState(false);
  const [hasAuditLogs, setHasAuditLogs] = useState(false);
  const [checkingAuditLogs, setCheckingAuditLogs] = useState(false);
  const [showSelectActionType, setShowSelectActionType] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);


  const { pathname } = useLocation();
  const holdingId = pathname.split('/')[2];
  const tenantConfigType = t('tenantConfigType');
  const printSummaryTitle = config.PRINT_SUMMARY.TITLE;

  const data: SageConsumerDataShape | null = useGetConsumerData(
    holdingId,
    undefined,
    shouldRefetch,
    t('tenantLink')
  );

  const { holding_proxy, allHoldings, consumer_private, vendable } = data ?? {};

  const isSerialNumberValidationEnabled = Boolean(
    vendable?.serialNumberEnabled ??
      config?.HOLDINGS?.SERIAL_NUMBER_VALIDATION?.ENABLED
  );

  const latestWarranty: Warranty | undefined = BL_Warranty.getLatestWarranty(
    holding_proxy?.warranties
  );
  const [voided, setVoided] = useState(
    latestWarranty?.status === WarrantyStatus.VOIDED
  );

  const CFG_COBRAND_REGISTRATIONS_PRINT_SUMMARY_OPTION = t(
    'CFG_COBRAND_REGISTRATIONS_PRINT_SUMMARY_OPTION'
  );

  const isPremiumTenant =
    userContext?.accessToken?.[
      PersonaTypeSingleLetter.Warrantor + PersonaIdTypeSplitter + tenantLink
    ] === 'S' || userContext?.accessToken?.admin === true;
  const isPartnerCanVoidWarranties =
    config?.WARRANTIES.MANAGEMENT.PARTNER_CAN_VOID_WARRANTIES &&
    activeTenantInfo?.docid && // Ensure docid exists
    userContext?.accessToken?.[
      `${PersonaTypeSingleLetter.WarrantyDealer}${PersonaIdTypeSplitter}${activeTenantInfo.docid}`
    ] === 'S';
  const isDealerPersona =
    activeTenantInfo?.docid &&
    userContext?.accessToken?.[
      `${PersonaTypeSingleLetter.WarrantyDealer}${PersonaIdTypeSplitter}${activeTenantInfo.docid}`
    ] === 'S';

  let tenantType: TenantType = 'defaultTenants';
  if (tenantLink === 'WARRANTYIRELAND' || tenantLink === 'PINNACLEWARRANTIES')
    tenantType = 'carTenants';
  if (tenantLink === 'NUCOVER') tenantType = 'tableTenants';
  const warrantyInfo = BL_Warranty.getLatestWarranty(holding_proxy?.warranties);

  const voidWarrantyHandler = async () => {
    setIsVoiding(true);
    try {
      if (latestWarranty && latestWarranty?.warrantyLink) {
        await warrantyVoid({
          warrantyLink: latestWarranty.warrantyLink,
        });
        toast.success(t('Warranty voided successfully!'));
        setVoidWarranty(undefined);
        setVoided(true);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setIsVoiding(false);
    }
  };

  useEffect(() => {
    setVoided(
      BL_Warranty.getLatestWarranty(holding_proxy?.warranties)?.status ===
        WarrantyStatus.VOIDED
    );
  }, [holding_proxy?.warranties]);

  useEffect(() => {
    if (holding_proxy?.purchase_location) {
      getRetailerLabelWithBusinessInvite(
        holding_proxy?.purchase_location,
        config
      )
        .then((res) =>
          setStoreAddress(res ?? holding_proxy?.purchase_location ?? '-')
        )
        .catch((err) => console.log(err));
    } else if (holding_proxy?.purchase_location_other) {
      setStoreAddress(
        holding_proxy?.purchase_location_other === 'Shopify'
          ? t('CFG_COBRAND_SHORT_NAME')
          : holding_proxy?.purchase_location_other
      );
    }
  }, [
    holding_proxy?.purchase_location,
    holding_proxy?.purchase_location_other,
  ]);

  useEffect(() => {
    if (vendable && isSerialNumberValidationEnabled) {
      setSerialNumbers(vendable.serialNumbers || []);
      setSerialNumberRanges(vendable.serialNumberRanges || []);
    }
  }, [vendable, isSerialNumberValidationEnabled]);

  const [favouriteStore, setFavouriteStore] = useState<string>('');

  useEffect(() => {
    if (allHoldings)
      (async () => {
        const res = await getFavouriteStore(allHoldings);
        if (res === '-' && holding_proxy?.purchase_location_other) {
          setFavouriteStore(
            holding_proxy?.purchase_location_other === 'Shopify'
              ? t('CFG_COBRAND_SHORT_NAME')
              : holding_proxy?.purchase_location_other
          );
        } else {
          setFavouriteStore(res);
        }
      })().catch((err) => console.log(err));
  }, [allHoldings]);

  // Check if there are any logs available for the history log button
  const warrantyHistoryData = useGetWarrantyHistory(
    latestWarranty?.warrantyLink || '',
    t('tenantLink')
  );

  const warrantyHistory = warrantyHistoryData?.warrantyHistory || null;
  const warrantyHistoryLoading =
    warrantyHistoryData?.warrantyHistoryLoading || false;

  // Use the hook to get audit logs
  const { auditLogs, isLoading: auditLogsLoading } = useGetAuditLogs({
    entityType: 'holding',
    entityLink: holding_proxy?.holdingLink,
    tenantLink: tenantLink,
    limitCount: 1, // We only need to know if any exist, so limit to 1
  });

  // Effect to check warranty logs
  useEffect(() => {
    // Check if there are warranty history logs available
    if (!warrantyHistoryLoading) {
      const warrantLogsExist =
        warrantyHistory !== null && warrantyHistory.length > 1;
      setHasWarrantyLogs(warrantLogsExist);
    }
  }, [warrantyHistory, warrantyHistoryLoading]);

  // Effect to check audit logs
  useEffect(() => {
    if (holding_proxy?.consumer_email) {
      const mightHaveAuditLogs =
        config?.HOLDINGS?.SHOW_AUDIT_LOG &&
        !!holding_proxy?.holdingLink &&
        !!tenantLink;

      if (mightHaveAuditLogs) {
        setCheckingAuditLogs(auditLogsLoading);
        setHasAuditLogs(auditLogs.length > 0);
      } else {
        setHasAuditLogs(false);
      }
    }
  }, [
    config?.HOLDINGS?.SHOW_AUDIT_LOG,
    holding_proxy?.holdingLink,
    tenantLink,
    holding_proxy,
    auditLogs,
    auditLogsLoading,
  ]);

  // Effect to update hasLogs when either warranty logs or audit logs change
  useEffect(() => {
    // Update hasLogs state based on warranty logs and audit logs
    setHasLogs(hasWarrantyLogs || hasAuditLogs);
  }, [hasWarrantyLogs, hasAuditLogs]);

  // Check if the path is from Warranty Overview and the user is not an owner/warrantor -VP
  if (
    pathname.includes(SAGE_ROUTE_NAME.WARRANTY_OVERVIEW) &&
    !userPermissions?.includes(Permissions.Owner)
  )
    return <Navigate to={'/'} />;
  else if (
    !userPermissions?.includes(Permissions.RegistrationsEdit) &&
    !userPermissions?.includes(Permissions.RegistrationsView) &&
    !userPermissions?.includes(Permissions.Owner)
  )
    return <Navigate to={'/'} />;

  const canInitiateNewClaim = () => {
    return (
      userPermissions.includes(Permissions.CasesEdit) ||
      userPermissions.includes(Permissions.Owner)
    );
  };
  const validWarrantyForClaim = () => {
    if (warrantyInfo?.status !== WarrantyStatus.ACTIVE) return false;
    return true;
  };

  const fetchWarrantyDetails = async () => {
    if (holding_proxy) {
      const warrantyDetails =
        tenantConfigType === 'vehicle'
          ? {
              consumer: data?.consumer_private,
              holding: holding_proxy,
              warranties: holding_proxy.warranties,
            }
          : {
              holding: holding_proxy,
              consumer: data?.consumer_private,
            };
      setWarrantyDetailsForClaim(warrantyDetails);
      setNewClaimModalOpen(true);
    } else {
      toast.error('Registration not found');
    }
  };

  const warrantyActions = [
    {
      key: 'edit',
      label: t('general.edit'),
      icon: PencilSquareIcon,
      visible: config.HOLDINGS.REGISTRATION.MANAGEMENT.EDIT_REGISTRATION,
      onClick: () => {
        handleEditClick();
        setShowWarrantyOptions(false);
      },
    },
    {
      key: 'history',
      label: t('general.historyLog'),
      icon: ClockIcon,
      visible: hasLogs,
      onClick: () => {
        setHistoryLog(true);
        setShowWarrantyOptions(false);
      },
    },
    {
      key: 'transfer',
      label: (
        <>
          {t('Transfer ownership')}
          {checkingClaims && (
            <LoadingSpinner extraClasses="w-4 h-4" size="xs" />
          )}
        </>
      ),
      icon: UserIcon,
      visible:
        config.HOLDINGS.ALLOW_CHANGE_CUSTOMER &&
        (userPermissions.includes(Permissions.Owner) || isDealerPersona),
      disabled: checkingClaims,
      onClick: async () => {
        setShowWarrantyOptions(false);
        if (holding_proxy?.holdingLink) {
          if (isDealerPersona && isRegistrationOlderThan7Days(holding_proxy)) {
            toast.error(
              t('Transfer disabled for registrations older than 7 days')
            );
            return;
          }
          const hasActiveClaims = await checkForActiveClaims(
            holding_proxy.holdingLink
          );
          if (!hasActiveClaims) setChangeCustomer(true);
        }
      },
    },
    {
      key: 'notes',
      label: (
        <>
          {t('general.notes')}
          {noteCount > 0 && (
            <span className="bg-primary-100 text-primary-900 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-xs font-medium">
              {noteCount}
            </span>
          )}
        </>
      ),
      icon: Bars3BottomLeftIcon,
      visible: config.HOLDINGS.NOTES_ENABLED,
      onClick: () => {
        setShowWarrantyOptions(false);
        setShowNotesModal(true);
      },
    },
    {
      key: 'print',
      label: t('general.printWarrantySummary'),
      icon: PrinterIcon,
      visible: CFG_COBRAND_REGISTRATIONS_PRINT_SUMMARY_OPTION === 'true',
      onClick: () => window.open(`${pathname}/print`, '_blank'),
    },
    {
      key: 'void',
      label: t('general.voidWarranty'),
      icon: DocumentMinusIcon,
      visible:
        (isPremiumTenant || isAdmin || isPartnerCanVoidWarranties) &&
        config?.WARRANTIES.MANAGEMENT.CAN_VOID_WARRANTIES,
      disabled: voided,
      onClick: () => {
        setVoidWarranty(holding_proxy?.docid);
        setShowWarrantyOptions(false);
      },
    },
    {
      key: 'editWarranty',
      label: t('general.editWarranty'),
      icon: PencilSquareIcon,
      visible:
        (isPremiumTenant || isAdmin) &&
        config?.WARRANTIES.MANAGEMENT.CAN_EDIT_WARRANTIES,
      disabled: voided,
      onClick: () => {
        setEditWarranty(holding_proxy?.docid);
        setShowWarrantyOptions(false);
      },
    },
    {
      key: 'claim',
      label: t('general.newClaim'),
      icon: DocumentMinusIcon,
      visible: canInitiateNewClaim() && validWarrantyForClaim(),
      disabled: voided,
      onClick: () => {
        fetchWarrantyDetails();
        setShowWarrantyOptions(false);
      },
    },
  ];

  const navigate = useNavigate();

  const handleUpdateHolding = async (formData: any) => {
    setIsLoading(true);
    try {

      let serialVerified = false;
      if (isSerialNumberValidationEnabled) {
        const serialNumber = formData.serial_number;
        const isValid = isSerialNumberValid(
          serialNumber,
          serialNumbers,
          serialNumberRanges
        );
        serialVerified = isValid;
      }

      // Before updating, ensure unique fields remain unique (excluding this holding)
      const additionalFields =
        config?.HOLDINGS?.NEW_REGISTRATION_FLOW?.ADDITIONAL_FIELDS || {};
      if (additionalFields && typeof additionalFields === 'object') {
        for (const fieldKey of Object.keys(additionalFields)) {
          const fieldCfg: any = (additionalFields as any)[fieldKey];
          const fieldValue: any = (formData as any)[fieldKey];

          if (
            fieldCfg?.validation?.unique &&
            typeof fieldValue === 'string' &&
            fieldValue.trim()
          ) {
            try {
              const mfr = `${
                PersonaTypeSingleLetter.Manufacturer
              }${PersonaIdTypeSplitter}${t('tenantLink')}`;
              const query = FBD_HoldingProxy.query()
                .where('mfr', '==', mfr)
                .where(`additionalData.${fieldKey}`, '==', fieldValue.trim());

              const matches = await query.getContents();
              const others = (matches || []).filter(
                (hp: any) => hp?.holdingLink !== holding_proxy?.holdingLink
              );
              if (others.length > 0) {
                toast.error(
                  t('message.uniqueCodeAlreadyExists', {
                    codeType: fieldCfg.label || fieldCfg.title || fieldKey,
                    code: fieldValue.trim(),
                  })
                );
                setIsLoading(false);
                return; // Abort update
              }
            } catch (err) {
              console.error(`Error checking unique field ${fieldKey}:`, err);
              toast.error(t('general.somethingWentWrongPleaseTryAgain'));
              setIsLoading(false);
              return; // Abort on error
            }
          }
        }
      }
      // Create the payload using the form data
      const payload: UpdateHoldingProxyRequest = {
        holdingLink: holding_proxy?.holdingLink || '',
        tenantLink: tenantLink,
        formData: {
          purchase_price: formData.purchase_price,
          vendable: formData.holding_vendable_id,
          purchase_time: config.HOLDINGS.REGISTRATION.CAN_EDIT_PURCHASE_DATE
            ? OurDateTime.dateToTimestamp(formData.purchase_time)
            : undefined,
          ...(formData.serial_number !== undefined ? { serial: formData.serial_number } : {}),
          ...(isSerialNumberValidationEnabled && { serialNumberVerified: serialVerified }),
          additionalData: {
            ...holding_proxy?.additionalData,
            ...Object.fromEntries(
              Object.entries(formData).filter(
                ([key]) =>
                  ![
                    'holding_vendable_id',
                    'purchase_time',
                    'purchase_price',
                    'serial_number',
                  ].includes(key)
              )
            ),
          },
        },
      };

      await nestApiUpdateHolding(payload);
      setEditRegistration(false);
      toast.success(t('Registration updated successfully'));
      setShouldRefetch((prev) => prev + 1);

      // Check if vendable has changed, if so redirect to the new URL
      if (formData.holding_vendable_id !== holding_proxy?.vendable) {
        navigate(
          `${SAGE_ROUTE_NAME.REGISTRATIONS}/${holding_proxy?.holdingLink}_${formData.holding_vendable_id}`
        );
      }
    } catch (error) {
      console.error('Error updating holding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkForActiveClaims = async (holdingLink: string) => {
    try {
      setCheckingClaims(true);
      const claimsQuery = FBD_CaseFlowPrivateProxy.query().where(
        'facts.consumer_holding',
        '==',
        holdingLink
      );
      const claims = await claimsQuery.getContents();
      const activeClaims = claims.filter(
        (claim) => claim.case_state !== 'closed'
      );
      if (activeClaims.length > 0) {
        toast.error(t('message.customerCannotBeChangedWhenClaimIsPending'));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking for active claims:', error);
      return false;
    } finally {
      setCheckingClaims(false);
    }
  };

  const hasMultipleRegs =
    (
      allHoldings?.filter(
        (h: any) => h.consumer_email === consumer_private?.email
      ) ?? []
    ).length > 1;

  const handleEditClick = () => {
    // Ticket ID: SA-271
    const registrationOlderThan7Days =
      isRegistrationOlderThan7Days(holding_proxy);

    if (!isDealerPersona) {
      setShowSelectActionType(true);
      return;
    }

    // Dealer persona & older than 7 days → cannot edit
    if (registrationOlderThan7Days) {
      toast.error('Editing disabled for registrations older than 7 days');
      return;
    }
    // Dealer persona & multiple registrations & older than 7 days → cannot edit
    if (hasMultipleRegs && registrationOlderThan7Days) {
      toast.error(
        'This customer cannot be modified as it has other registrations and is older than 7 days. Please contact support for more help.'
      );
      return;
    }
    // Dealer persona & multiple registrations but within 7 days → can only edit registration details
    if (hasMultipleRegs && !registrationOlderThan7Days) {
      setShowSelectActionType(true);
      return;
    }

    // Otherwise allow normal editing
    setShowSelectActionType(true);
  };

  if (!holding_proxy || !consumer_private)
    return <LoadingSpinner size={'sm'} />;

  return (
    <div className="flex flex-col gap-5">
      {voided && (
        <div className="flex flex-col gap-2 rounded-md bg-red-100 p-4">
          <div className="font-nunito flex items-center gap-1 font-bold text-red-700">
            <InformationCircleIcon className="h-4 w-4 text-red-700" />{' '}
            {t('Warranty voided at')}{' '}
            {formatUnixTime(holding_proxy?.tupdate as number, 'dd/MM/yyyy')}
          </div>
          <span className="font-nunito text-red-700">
            {t('This warranty has been voided.')}
          </span>
        </div>
      )}

      {holding_proxy && (
        <>
          <div className="relative ml-auto inline-block">
            {config?.WARRANTIES.MANAGEMENT.SHOW_WARRANTY_OPTIONS_BTN &&
              warrantyActions.some((action) => action.visible) && (
                <ButtonIcon
                  kind="bgLightGreen"
                  Icon={Cog8ToothIcon}
                  onClick={() => setShowWarrantyOptions((prev) => !prev)}
                  label={t('general.actions')}
                  iconLeft
                  aria-haspopup="menu"
                  aria-expanded={showWarrantyOptions}
                  aria-controls="warranty-actions-menu"
                />
              )}

            {showWarrantyOptions && (
              <div
                role="menu"
                aria-label={t('general.actions')}
                className="absolute top-12 right-0 z-10"
              >
                <div className="flex h-fit w-[calc(100vw-24px)] flex-col rounded-lg bg-white p-3 shadow-lg md:w-80">
                  <XMarkIcon
                    className="m-1 ml-auto h-5 w-5 cursor-pointer text-gray-500"
                    onClick={() => setShowWarrantyOptions(false)}
                    role="button"
                    aria-label={t('general.close')}
                  />

                  <ul className="flex flex-col space-y-1">
                    {warrantyActions
                      .filter((action) => action.visible)
                      .map(({ key, label, icon: Icon, disabled, onClick }) => (
                        <li key={key} role="none">
                          <button
                            role="menuitem"
                            className="inline-flex w-full items-center gap-2 rounded p-2 text-base transition-all hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={onClick}
                            disabled={disabled}
                          >
                            <Icon className="h-4 w-4" />
                            {label}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* action item modals */}
          <>
            {historyLog && latestWarranty?.warrantyLink && (
              <ModalHistoryLog
                handleClose={() => setHistoryLog(false)}
                warrantyLink={latestWarranty?.warrantyLink}
                entityType="holding"
                entityLink={holding_proxy?.holdingLink}
                tenantLink={tenantLink}
              />
            )}
            {changeCustomer && (
              <ModalCreateNewSelectUser
                handleClose={() => setChangeCustomer(false)}
                holdingId={holding_proxy.docid}
                holdingLink={holding_proxy.holdingLink}
                onCustomerChanged={() => setShouldRefetch((prev) => prev + 1)}
                oldEmail={holding_proxy.consumer_email}
              />
            )}
            {showSelectActionType && (
              <ModalSelectActionType
                handleClose={() => setShowSelectActionType(false)}
                options={[
                  {
                    value: 'customer',
                    label: 'Change Customer Details',
                    description: isDealerPersona
                      ? hasMultipleRegs
                        ? 'Edit disabled customer has multiple registrations.'
                        : 'Update customer details within 7 days of registration.'
                      : undefined,
                    disabled: Boolean(
                      isDealerPersona &&
                        (isRegistrationOlderThan7Days(holding_proxy) ||
                          hasMultipleRegs)
                    ),
                  },
                  {
                    value: 'registration',
                    label: 'Change Registration Details',
                    description: isDealerPersona
                      ? 'Update registration details within 7 days of registration.'
                      : undefined,
                    disabled: Boolean(
                      isDealerPersona &&
                        isRegistrationOlderThan7Days(holding_proxy)
                    ),
                  },
                ]}
                onContinue={(selected) => {
                  if (selected === 'customer') {
                    setIsEditCustomerModalOpen(true);
                  } else {
                    setEditRegistration(true);
                  }
                  setShowSelectActionType(false);
                }}
              />
            )}
            {isEditCustomerModalOpen && (
              <ModalNewEditCustomer
                handleClose={() => {
                  setIsEditCustomerModalOpen(false);
                  setShouldRefetch((prev) => prev + 1);
                }}
                handleDelete={() => {
                  setIsEditCustomerModalOpen(false);
                  setShouldRefetch((prev) => prev + 1);
                }}
                customerDetails={consumer_private}
                modalSettings={{
                  title: t('general.editCustomer'),
                  handleClose: () => {
                    setIsEditCustomerModalOpen(false);
                    setShouldRefetch((prev) => prev + 1);
                  },
                }}
                customerType={consumer_private?.consumer_type}
              />
            )}
            {editRegistration && (
              <ModalEditDynamicForm
                title={t('general.editRegistration')}
                tenantLink={t('tenantLink')}
                handleSubmit={handleUpdateHolding}
                formType={FormSchemaEnum.REGISTRATION}
                isLoading={isLoading}
                initialData={{
                  holding_vendable_id: holding_proxy?.vendable || '',
                  purchase_price: {
                    amount: holding_proxy?.purchase_price?.amount,
                    currency: holding_proxy?.purchase_price?.currency,
                  },
                  interior: holding_proxy?.additionalData?.interior || '',
                  make: holding_proxy?.additionalData?.make || '',
                  model: holding_proxy?.additionalData?.model || '',
                  vehicleNo: holding_proxy?.additionalData?.vehicleNo || '',
                  color: holding_proxy?.additionalData?.color || '',
                  newVehicle:
                    holding_proxy?.additionalData?.newVehicle || false,
                  vehicleType: holding_proxy?.additionalData?.vehicleType || '',
                  uniqueCode: holding_proxy?.additionalData?.uniqueCode || '',
                  leatherUniqueCode:
                    holding_proxy?.additionalData?.leatherUniqueCode || '',
                  vin: holding_proxy?.additionalData?.vin || '',
                  purchase_time: holding_proxy?.purchase_time || '',
                  salesperson: holding_proxy?.additionalData?.salesperson || '',
                  salesReferenceNo:
                    holding_proxy?.additionalData?.salesReferenceNo || '',
                  serial_number: holding_proxy?.serial || '',
                }}
                handleClose={() => setEditRegistration(false)}
              />
            )}
          </>
        </>
      )}
      {showNotesModal && (
        <Notes
          entityId={holding_proxy.docid}
          entityType={NoteEntityType.HOLDING}
          showModal={showNotesModal}
          setShowModal={setShowNotesModal}
          onNotesCountChange={(count) => setNoteCount(count)}
        />
      )}

      {tenantType === 'carTenants' ? (
        <CarRegistrationsHoldingSummarySection
          allHoldingsList={allHoldings ?? undefined}
          holdingId={holdingId}
          holding_proxy={holding_proxy ?? undefined}
          storeAddress={storeAddress}
        />
      ) : (
        <RegistrationsHoldingSummarySection
          allHoldingsList={allHoldings ?? undefined}
          holdingId={holdingId}
          holding_proxy={holding_proxy ?? undefined}
          storeAddress={storeAddress}
        />
      )}
      {t('tenantLink') === 'PINNACLEWARRANTIES' && (
        <RegistrationsWarrantyDetailsSectionCarWarranty
          holding_proxy={holding_proxy}
          warrantyInfo={latestWarranty}
          title="Service plan details"
        />
      )}
      {t('tenantLink') !== 'PINNACLEWARRANTIES' && (
        <WarrantyViewer
          warranties={holding_proxy.warranties}
          tenantType={tenantType}
          tenantInfo={tenantInfo}
        />
      )}
      {config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS &&
        Object.keys(holding_proxy.additionalData || {}).length > 0 && (
          <AdditionalDetailsView
            title={'Additional Details'}
            holdingProxy={holding_proxy}
            isPartnerToTenant={!!isPartnerToTenant}
            isCustomerPersona={false}
            config={config}
          />
        )}
      {t('tenantLink') === 'TRANSPORTER' && (
        <WarrantyDetailsSection
          holding_proxy={holding_proxy}
          allHoldingsList={allHoldings}
          consumer_private={consumer_private}
          storeAddress={storeAddress}
          holdingId={holdingId}
        />
      )}

      {!consumerPersona?.personaId && (
        <RegistrationsCustDetailsSection
          allHoldingsList={allHoldings ?? undefined}
          consumer_private={consumer_private ?? undefined}
          favouriteStore={favouriteStore}
        />
      )}

      <RegistrationsHoldingDetailsSection
        holding_proxy={holding_proxy}
        allHoldingsList={allHoldings}
        consumer_private={consumer_private}
        storeAddress={storeAddress}
        holdingId={holdingId}
        vendable={vendable}
      />

      {config.INSTALLATION_REQUIRED && (
        <InstallationDetails
          holding_proxy={holding_proxy}
          allHoldingsList={allHoldings}
          consumer_private={consumer_private}
          storeAddress={storeAddress}
          holdingId={holdingId}
        />
      )}

      {t('tenantLink') === 'SHELTA' && (
        <RegistrationsOtherProductsSection
          allHoldingsList={
            allHoldings?.filter(
              (h) =>
                h.docid !== holdingId && h.full !== data?.holding_proxy?.full
            ) ?? null
          }
        />
      )}
      {config.HOLDINGS.NOTES_SECTION && (
        <NotesDetailContentSection
          entityId={holdingId}
          entityType={NoteEntityType.HOLDING}
        />
      )}
      {voidWarranty && (
        <Modal
          isLoading={isVoiding}
          className="lg:max-w-[350px]"
          kind="generic"
          settings={{ handleClose: () => setVoidWarranty(undefined) }}
        >
          <div className="font-nunito flex flex-col gap-3 px-6 pt-6 pb-3">
            <div className="flex flex-col items-center gap-4">
              <InformationCircleIcon className="h-8 w-8 text-gray-400" />
              <div className="text-md text-center text-lg text-gray-500">
                {t(
                  `You are about to void this ${printSummaryTitle}. Are you sure you want to continue?`
                )}
              </div>
              <div className="text-medium text-gray-900">
                {t('To confirm this, type the ')}
                <strong>{t(`${printSummaryTitle} ID`)}</strong> {t('below')}.
              </div>
              <input
                type="text"
                className="font-nunito focus:border-primary-600 w-full rounded-lg border-[1px] border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-800 placeholder-gray-400 !shadow-none !outline-none !ring-0"
                placeholder={`[${printSummaryTitle} ID]`}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <Button
                kind="primary"
                type="submit"
                loading={isVoiding}
                disabled={
                  confirmation !==
                  warrantyInfo?.warrantyLink?.slice(0, 5).toUpperCase()
                }
                onClick={() => voidWarrantyHandler()}
              >
                {t(`Void ${printSummaryTitle}`)}
              </Button>
              <Button
                kind="outline"
                type="button"
                onClick={() => setVoidWarranty(undefined)}
              >
                {t('No, cancel')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {editWarranty && latestWarranty?.warrantyLink && (
        <ModalEditWarranty
          handleClose={() => setEditWarranty(undefined)}
          warrantyLink={latestWarranty?.warrantyLink}
        />
      )}
      {newClaimModalOpen &&
        (tenantConfigType === 'vehicle' ? (
          <ModalNewClaimVehicle
            data={warrantyDetailsForClaim}
            handleClose={() => setNewClaimModalOpen(false)}
          />
        ) : (
          <ModalNewClaim
            data={warrantyDetailsForClaim}
            handleClose={() => setNewClaimModalOpen(false)}
          />
        ))}
    </div>
  );
}

export default RegistrationsDetailsView;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const getRetailerLabelWithBusinessInvite = async (
  locationId: string,
  config: ConfigInterface['config']
) => {
  if (!locationId) return undefined;

  // First try the common getRetailerLabel function
  const retailerLabel = await getRetailerLabel(locationId);

  if (retailerLabel) {
    return retailerLabel;
  }

  // If no retailer found, try business invite (RegistrationsDetailsView specific logic)
  if (config.HOLDINGS.NEW_REGISTRATION_FLOW.STORES_FROM_FIRESTORE_RETAILERS) {
    const businessInvite = await FBD_Business_User_Invite.get(locationId);
    if (businessInvite?.orgName) {
      const town = businessInvite.addresses?.[0]?.town;
      return town
        ? `${businessInvite.orgName} - ${town}`
        : businessInvite.orgName;
    }
  }

  return undefined;
};
